import { Component, Input, OnInit } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Product } from '../../shared/model/product.model';
import { ProductsService } from '../../shared/services/products.service';
import { LocalesService } from '../../shared/services/locales.service';
import { TimezonesService } from '../../shared/services/timezones.service';
import { Locale } from '../../shared/model/locale.model';
import { Timezone } from '../../shared/model/timezone.model';
import { CookieService as NgxCookieService } from 'ngx-shared-services';

@Component({
  selector: 'app-nested-product',
  templateUrl: './nested-product-details.component.html',
  styleUrls: ['./nested-product-details.component.scss', '../../shared/shared.styles.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class NestedProductDetailsComponent {
  readonly EDIT = 'edit';
  readonly VIEW = 'view';
  readonly CREATE = 'create';
  panelOpenedState: boolean;
  locales: Locale[] = [];
  timezones: Timezone[] = [];
  userLocale = 'en_US';

  private _product: Product;
  private _index: number;
  private _data: Record<string, never>;
  private _mode: string;

  constructor(
    private productsService: ProductsService,
    private router: Router,
    private localesService: LocalesService,
    private timezonesService: TimezonesService,
    private ngxCookieService: NgxCookieService
  ) {
    this.localesService.locales$.subscribe(locales => this.locales = locales);
    this.userLocale = this.ngxCookieService.getCookie('locale') || 'en_US';
    this.timezones = timezonesService.timezones;
  }

  @Input()
  set product(product: Product) {
    this._product = product;
  }

  get product() {
    return this._product;
  }

  @Input()
  set index(index: number) {
    this._index = index;
  }

  get index() {
    return this._index;
  }

  @Input()
  set data(data: Record<string, never>) {
    this._data = data;
  }

  get data() {
    return this._data;
  }

  @Input()
  set mode(mode: string) {
    this._mode = mode;
  }

  get mode() {
    return this._mode;
  }

  getDisplayLabels(): string {
    return this.localesService.getValueFromMultilocaleObject(this._product.displayLabels, this.userLocale, this._product.defaultLocale);
  }

  getDescriptions(): string {
    return this.localesService.getValueFromMultilocaleObject(this._product.descriptions, this.userLocale, this._product.defaultLocale);
  }

  get status() {
    if (this._product) {
      return this._product.status;
    }
  }

  get timezone() {
    if (this._product && this.timezones.length > 0) {
      const timezone = this.timezones.find((timezone) => this._product.timezone === timezone.timezoneName);
      if (timezone) {
        return timezone.displayLabel;
      }
    }
  }

  get locale() {
    if (this._product && this.locales.length > 0) {
      const locale = this.locales.find((locale) => this._product.defaultLocale === locale.localeName);
      if (locale) {
        return locale.displayLabel;
      }
    }
  }

  get isEditMode() {
    return this.mode === this.EDIT;
  }

  get isCreateMode() {
    return this.mode === this.CREATE;
  }

  get isViewMode() {
    return this.mode === this.VIEW;
  }

  handleViewDetails($e) {
    $e.stopPropagation();
    if (this._product) {
      this.router.navigate([`details/${this._product.id}/view`], { queryParams: { drType: this._product.drType } });
    }
  }

  addProduct() {
    if (this._product) {
      this.router.navigate([`details/${this._product.id}/create`], { queryParams: { drType: this._product.drType } });
    }
  }

  handleEditDetails() {
    if (this._product) {
      this.router.navigate([`details/${this._product.id}/edit`], { queryParams: { drType: this._product.drType } });
    }
  }

  deleteProduct() {
    if (this._product) {
      const { isDispatchableProduct } = this._product;
      this.productsService.deleteProduct(this._product.id, isDispatchableProduct);
    }
  }
}
